import type { HttpClient } from '../http/HttpClient'
import { AGING_BUCKETS } from '@/domain/analytics/ApprovalAnalytics'
import type {
  AgingBucket,
  AnalyticsDocument,
  AnalyticsExportFormat,
  AnalyticsFilterOptions,
  AnalyticsSociety,
  ApprovalAges,
  ApprovalAnalytics,
  ApprovalAnalyticsFilters,
  ApprovalAnalyticsRepository,
  ApprovalGroup,
  ApprovalTimes,
  StepAging,
  TrendGranularity,
} from '@/domain/analytics/ApprovalAnalytics'

export const PENDING_BUCKET_KEYS = {
  onTime: 'pending_on_time',
  dueSoon: 'pending_due_soon',
  overdue: 'pending_overdue',
  noSla: 'pending_no_sla',
} as const satisfies Record<AgingBucket, string>

type RawBuckets = Record<(typeof PENDING_BUCKET_KEYS)[AgingBucket], number>

type RawStepAging = Omit<StepAging, AgingBucket> & RawBuckets

interface RawTimes {
  resolved: number
  median_hours: number | null
  on_time_pct: number | null
  measured?: number
  pending: number
}

type RawAges = RawTimes & RawBuckets & {
  oldest_days: number | null
  breakdown: RawStepAging[]
}

type RawPerson = RawAges & {
  id: number
  name: string
  picture?: string | null
  area: string | null
  society: string | null
  is_viewer: boolean
  is_active: boolean
}

type RawGroup = RawAges & {
  id: string | number | null
  name: string | null
  society?: string | null
  document?: AnalyticsDocument | null
  people?: number[]
}

interface RawBar {
  first_day: string
  last_day: string
  resolved: number
  measured?: number
  on_time: number
  median_hours: number | null
  partial: boolean
  current: boolean
}

interface RawApprovalAnalytics {
  documents: AnalyticsDocument[]
  societies: AnalyticsSociety[]
  totals: RawTimes & { overdue: number }
  people: RawPerson[]
  groups: { area: RawGroup[], flow: RawGroup[], cost_center: RawGroup[], society: RawGroup[] }
  period: { first_day: string, last_day: string, days: number }
  trend: { granularity: TrendGranularity, buckets: RawBar[] }
}

interface RawFilterOptions {
  areas: { id: number, name: string, code: string | null, society_name: string }[]
  cost_centers: { id: number, name: string, code: string | null }[]
  policies: { id: number, name: string }[]
  request_types: string[]
}

function filterParams(filters: ApprovalAnalyticsFilters): Record<string, string> {
  const params: Record<string, string> = {}
  if (filters.dateFrom && filters.dateTo) {
    params.date_from = filters.dateFrom
    params.date_to = filters.dateTo
  }
  if (filters.document) params.document = filters.document
  const lists: [string, (string | number)[]][] = [
    ['societies', filters.societies],
    ['areas', filters.areas],
    ['cost_centers', filters.costCenters],
    ['request_types', filters.requestTypes],
    ['policies', filters.policies],
  ]
  for (const [name, values] of lists) {
    if (values.length) params[name] = values.join(',')
  }
  return params
}

function times(raw: RawTimes): ApprovalTimes {
  return { resolved: raw.resolved, medianHours: raw.median_hours, onTimePct: raw.on_time_pct, measured: raw.measured ?? 0, pending: raw.pending }
}

function buckets(raw: RawBuckets): Record<AgingBucket, number> {
  return Object.fromEntries(AGING_BUCKETS.map((bucket) => [bucket, raw[PENDING_BUCKET_KEYS[bucket]] ?? 0])) as Record<AgingBucket, number>
}

function step(raw: RawStepAging): StepAging {
  return { document: raw.document, step: raw.step, title: raw.title, total: raw.total, ...buckets(raw) }
}

function ages(raw: RawAges): ApprovalAges {
  return { ...times(raw), oldestDays: raw.oldest_days, ...buckets(raw), breakdown: raw.breakdown.map(step) }
}

function group(raw: RawGroup): ApprovalGroup {
  return { ...ages(raw), id: raw.id, name: raw.name, society: raw.society ?? null, document: raw.document ?? null, people: raw.people ?? [] }
}

export class HttpApprovalAnalyticsRepository implements ApprovalAnalyticsRepository {
  constructor(private readonly http: HttpClient) {}

  async approvals(filters: ApprovalAnalyticsFilters): Promise<ApprovalAnalytics> {
    const raw = await this.http.url('purchases/analytics/approvals').params(filterParams(filters)).get<RawApprovalAnalytics>()
    return {
      documents: raw.documents,
      societies: raw.societies,
      totals: { ...times(raw.totals), overdue: raw.totals.overdue },
      people: raw.people.map((person) => ({
        ...ages(person),
        id: person.id,
        name: person.name,
        picture: person.picture ?? null,
        area: person.area,
        society: person.society,
        isViewer: person.is_viewer,
        isActive: person.is_active,
      })),
      groups: {
        area: raw.groups.area.map(group),
        flow: raw.groups.flow.map(group),
        costCenter: raw.groups.cost_center.map(group),
        society: raw.groups.society.map(group),
      },
      period: { firstDay: raw.period.first_day, lastDay: raw.period.last_day, days: raw.period.days },
      trend: {
        granularity: raw.trend.granularity,
        bars: raw.trend.buckets.map((bar) => ({
          firstDay: bar.first_day,
          lastDay: bar.last_day,
          resolved: bar.resolved,
          measured: bar.measured ?? 0,
          onTime: bar.on_time,
          medianHours: bar.median_hours,
          partial: bar.partial,
          current: bar.current,
        })),
      },
    }
  }

  async exportUrl(filters: ApprovalAnalyticsFilters, format: AnalyticsExportFormat): Promise<string> {
    const params = { ...filterParams(filters), file_format: format }
    const raw = await this.http.url('purchases/analytics/approvals/export').params(params).get<{ url: string }>()
    return raw.url
  }

  async filterOptions(): Promise<AnalyticsFilterOptions> {
    const raw = await this.http.url('purchases/analytics/approvals/filters').get<RawFilterOptions>()
    return {
      areas: raw.areas.map((area) => ({ id: area.id, name: area.name, code: area.code, societyName: area.society_name })),
      costCenters: raw.cost_centers,
      policies: raw.policies,
      requestTypes: raw.request_types,
    }
  }
}
